import type { Position, PositionNames, Vendor, Category } from '../types';
import { parsePosition } from '../features/position/fromRow';
import { vendorRowToVendor } from '../features/vendor/fromRow';
import { resolveCategoryColor } from '../utils/categoryColors';
export { DEFAULT_CATEGORY_COLOR } from '../utils/categoryColors';
import {
  AMENITY_CATEGORY,
  AMENITY_CATEGORY_ID,
  ENTERTAINMENT_CATEGORY,
  ENTERTAINMENT_CATEGORY_ID,
  isAmenityType,
  synthesizeAmenityVendor,
} from '../utils/amenities';
import type { CategoryRow, NameRow, PositionRow, VendorRow } from '../validators/sheets';

export interface SheetsResult {
  vendors: Vendor[];
  positions: Position[];
  categories: Category[];
  names: PositionNames;
  positionWarnings: string[];
}

export function rowsToPositions(rows: PositionRow[]): { positions: Position[]; warnings: string[] } {
  const seen = new Set<string>();
  const warnings: string[] = [];
  const positions = rows
    .map((r) =>
      parsePosition({
        position_id: r.position_id,
        coordinates: r.coordinates,
        type: r.type,
        label_position: r.label_position,
        name: r.name,
        description: r.description,
      }),
    )
    .filter((p) => {
      if (!p.positionId) return false;
      // Same positionId + same coordinates = accidental duplicate row.
      // Same positionId + different coordinates = intentional multi-position vendor (#71).
      // parseCoordinates returns JS numbers, so Number.toString() normalises "800.0" and "800"
      // to the same string — float-variant duplicates dedupe correctly without explicit rounding.
      // positionId is lowercased here; joinData also lowercases both sides, so casing is consistent.
      const key = `${p.positionId.toLowerCase()}:${p.coordinates[0]},${p.coordinates[1]}`;
      if (seen.has(key)) {
        const msg = `Duplicate position_id "${p.positionId}" at [${p.coordinates}] — ignoring extra row.`;
        console.warn(`[market-map] ${msg}`);
        warnings.push(msg);
        return false;
      }
      seen.add(key);
      return true;
    });
  return { positions, warnings };
}

export function rowsToCategories(rows: CategoryRow[]): Category[] {
  return rows.map((r) => ({ id: r.id, name: r.name, color: resolveCategoryColor(r.color) }));
}

export function rowsToNames(rows: NameRow[]): PositionNames {
  const map: PositionNames = {};
  for (const r of rows) map[r.prefix] = r.name;
  return map;
}

export function joinData(
  vendors: VendorRow[],
  positions: Position[],
  categories: Category[],
): Vendor[] {
  const posByPid = new Map<string, Position[]>();
  positions.forEach((p) => {
    const key = p.positionId.toLowerCase();
    const arr = posByPid.get(key) ?? [];
    arr.push(p);
    posByPid.set(key, arr);
  });

  const catByLower = new Map(categories.map((c) => [c.id.toLowerCase(), c.id]));

  const result: Vendor[] = [];
  // Slot-sharing rotation is expressed via the `here` flag — staff sets
  // here=false on the absent vendor each weekend. If two active here-vendors
  // somehow end up at the same slot, render the first and warn so it gets
  // fixed; silent stacking would be confusing on the map and ambiguous on
  // the list (which detail sheet does the marker tap open?).
  const occupiedPositionUids = new Set<string>();
  for (const v of vendors) {
    const matches = posByPid.get(v.position_id.toLowerCase());
    if (!matches || matches.length === 0) continue;
    if (v.here === false) continue;
    for (const pos of matches) {
      // Amenity-typed positions get a synthetic vendor regardless of the
      // sheet — never let a real Vendors-tab row hijack one of those slots.
      if (isAmenityType(pos.type)) continue;
      const vendor = vendorRowToVendor(v, pos.coordinates, pos.type, pos.labelPosition, pos.uid);
      const resolvedCat = catByLower.get(vendor.categoryId.toLowerCase());
      if (resolvedCat) vendor.categoryId = resolvedCat;
      if (vendor.status !== 'active') continue;
      if (occupiedPositionUids.has(pos.uid)) {
        console.warn(
          `[vendors] Position ${JSON.stringify(pos.positionId)} has multiple active "here" vendors; ` +
            `rendering the first and skipping ${JSON.stringify(v.name)}. ` +
            `Set here=false on the absent vendor to silence this warning.`,
        );
        continue;
      }
      occupiedPositionUids.add(pos.uid);
      result.push(vendor);
    }
  }

  // Synthetic vendors for amenity-typed Position rows — keeps amenities
  // first-class (search, filter, list, detail sheet) without requiring a
  // Vendors-tab row from staff.
  for (const pos of positions) {
    const synthetic = synthesizeAmenityVendor(pos);
    if (synthetic) result.push(synthetic);
  }

  return result;
}

/** Append synthetic categories (Amenities, Entertainment) to the categories
 *  list as needed, idempotent. Each chip only appears when at least one
 *  matching Position type exists — keeps the filter UI clean for markets
 *  that haven't authored those positions yet. */
export function ensureAmenityCategory(categories: Category[], positions: Position[]): Category[] {
  let result = categories;
  const hasNonStageAmenity = positions.some(p => isAmenityType(p.type) && p.type !== 'stage');
  const hasStage = positions.some(p => p.type === 'stage');
  if (hasNonStageAmenity && !result.some(c => c.id.toLowerCase() === AMENITY_CATEGORY_ID)) {
    result = [...result, AMENITY_CATEGORY];
  }
  if (hasStage && !result.some(c => c.id.toLowerCase() === ENTERTAINMENT_CATEGORY_ID)) {
    result = [...result, ENTERTAINMENT_CATEGORY];
  }
  return result;
}
